/**
 * What every section of an item's form shares: the document being edited and the way to change it
 * by path, the issues to show (a field shows its own once the editor has left it, every field after
 * a save was tried), the brand's languages, and the whole catalogue for pickers.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { entities, type Entity, type EntityType } from "@/api/admin";
import { useStudio } from "@/shell/studio";
import type { Issue } from "./checks";
import type { FormPlan } from "./plan";
import { setPath, type Doc } from "./paths";

export interface FormApi {
  type: EntityType;
  id: string;
  doc: Doc;
  plan: FormPlan;
  canEdit: boolean;
  locale: string;
  locales: string[];
  set: (path: string, value: unknown, keepEmpty?: boolean) => void;
  update: (change: (doc: Doc) => Doc) => void;
  /** The first issue shown for exactly this path. */
  issue: (path: string) => Issue | undefined;
  /** Shown issues at this path or under it (a list shows its rows' issues). */
  issuesUnder: (path: string) => Issue[];
  /** The editor left this field: show its issues from now on. */
  touch: (path: string) => void;
  /** Every item of the brand with its document (for picker cards), null while loading. */
  catalogue: Entity[] | null;
}

const Context = createContext<FormApi | null>(null);

export const FormProvider = Context.Provider;

export function useForm(): FormApi {
  const form = useContext(Context);
  if (!form) throw new Error("useForm outside FormProvider");
  return form;
}

/** Builds the form's API over the editor's state. */
export function useFormApi(input: { type: EntityType; id: string; doc: Doc; setDoc: (next: Doc | ((d: Doc) => Doc)) => void; plan: FormPlan; canEdit: boolean; issues: Issue[]; shownAll: boolean }): FormApi & { touched: Set<string>; resetTouched: () => void } {
  const { client } = useStudio();
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const catalogue = useCatalogue();
  const visible = (i: Issue) => input.shownAll || [...touched].some((t) => i.path === t || i.path.startsWith(`${t}.`));
  const { setDoc } = input;
  const set = useCallback((path: string, value: unknown, keepEmpty?: boolean) => setDoc((d) => setPath(d, path, value, keepEmpty)), [setDoc]);
  return {
    type: input.type, id: input.id, doc: input.doc, plan: input.plan, canEdit: input.canEdit,
    locale: client.brand.locales.default, locales: client.brand.locales.supported,
    set, update: (change) => setDoc(change),
    issue: (path) => input.issues.find((i) => i.path === path && visible(i)),
    issuesUnder: (path) => input.issues.filter((i) => (i.path === path || i.path.startsWith(`${path}.`)) && visible(i)),
    touch: (path) => setTouched((t) => (t.has(path) ? t : new Set(t).add(path))),
    catalogue, touched, resetTouched: () => setTouched(new Set()),
  };
}

/** Every item with its document, fetched again when the studio's list changes (after any save). */
export function useCatalogue(): Entity[] | null {
  const { client, items } = useStudio();
  const [all, setAll] = useState<Entity[] | null>(null);
  useEffect(() => {
    let live = true;
    entities.listFull(client).then((r) => { if (live) setAll(r.entities); }).catch(() => { if (live) setAll((a) => a ?? []); });
    return () => { live = false; };
  }, [client, items]);
  return all;
}
